import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { fmtKg } from "@/lib/format";
import { ArrowUpRight, Lightning } from "@phosphor-icons/react";

const PACKAGE_COLOR = {
  Substructure: "#6E59C7",
  Superstructure: "#D4AF37",
  Envelope: "#38BDF8",
  Facade: "#10B981",
  Roof: "#F59E0B",
  MEP: "#EF4444",
  Internal: "#A78BFA",
  Finishes: "#F472B6",
  Externals: "#94A3B8",
  Other: "#64748B",
};

function daysBetween(a, b) {
  return Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
}

/** Compact Gantt strip used on the Executive Dashboard. */
export default function MiniGantt() {
  const { active } = useProjects();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!active) return;
    api
      .get(`/projects/${active.id}/schedule`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [active]);

  if (!data) {
    return (
      <div className="border border theme-border theme-surface p-6 theme-ink-dim text-sm">
        Loading programme…
      </div>
    );
  }

  const total = daysBetween(data.programme_start, data.programme_end);

  return (
    <div data-testid="mini-gantt" className="border border theme-border theme-surface">
      <div className="px-6 pt-5 pb-3 flex items-start justify-between border-b theme-border">
        <div>
          <div className="overline">5D · Carbon × Cost × Schedule</div>
          <h3 className="font-serif-display text-2xl theme-ink mt-1">Programme Twin</h3>
        </div>
        <Link
          to="/schedule"
          data-testid="open-schedule-link"
          className="text-[11px] uppercase tracking-wider text-gold hover:underline flex items-center gap-1"
        >
          Open full Gantt <ArrowUpRight size={11} />
        </Link>
      </div>

      <div className="p-6">
        <div className="flex flex-col gap-1.5">
          {data.tasks.slice(0, 6).map((t) => {
            const offset = daysBetween(data.programme_start, t.start_date) - 1;
            const dur = daysBetween(t.start_date, t.end_date);
            const left = (offset / total) * 100;
            const width = Math.max(3, (dur / total) * 100);
            const color = PACKAGE_COLOR[t.package] || "#64748B";
            return (
              <div key={t.package} className="grid grid-cols-[110px_1fr_60px] items-center gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs theme-ink truncate">{t.package}</span>
                  {t.critical_path && <Lightning size={10} weight="fill" className="text-gold shrink-0" />}
                </div>
                <div className="relative h-4 bg-black/10 dark:bg-white/[0.03] rounded-sm">
                  <div
                    className="absolute top-0 bottom-0 rounded-sm"
                    style={{ left: `${left}%`, width: `${width}%`, backgroundColor: color, opacity: 0.85 }}
                    title={`${t.package} · ${t.start_date} → ${t.end_date} · ${fmtKg(t.embodied_kg)}`}
                  />
                </div>
                <div className="text-right text-[10px] font-mono-data theme-ink-dim">{t.share_pct}%</div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t theme-border flex items-center justify-between text-[11px] font-mono-data theme-ink-dim">
          <span>{data.programme_start} → {data.programme_end}</span>
          <span>{fmtKg(data.total_embodied_kg)} total embodied</span>
        </div>
      </div>
    </div>
  );
}
